// src/hooks/useSoftphoneEntitlement.ts — may this user have a browser softphone?
//
// The softphone is only for organisations that have PURCHASED seats
// (2026-10-14). The server decides — GET /telephony/softphone/entitlement — from
// the organisation's seat allocation; master/platform admins and the platform
// root organisation (Green Giraffe) are always entitled. The portal only reads
// the answer, so the sidebar, the Softphone page and the widget cannot disagree
// with the API that actually refuses.
//
// Cached per organisation id, like useFeatureAccess: switching organisations
// re-asks rather than carrying the previous tenant's answer.

import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';

export interface SoftphoneEntitlement {
  allowed: boolean;
  unlimited: boolean;
  maxSeats: number | null;
  usedSeats: number;
  holdsSeat: boolean;
  /** Human explanation, shown verbatim when not allowed. */
  reason: string;
}

type Entry = { value?: SoftphoneEntitlement; inflight?: Promise<void> };
const cache = new Map<string, Entry>();

export function clearSoftphoneEntitlementCache(): void {
  cache.clear();
}

/** The body arrives wrapped in ApiResponse ({ success, data }); tolerate both. */
export function parseEntitlement(body: any): SoftphoneEntitlement | undefined {
  const d = body?.data ?? body;
  if (!d || typeof d.allowed !== 'boolean') return undefined;
  return {
    allowed: d.allowed,
    unlimited: d.unlimited === true,
    maxSeats: typeof d.maxSeats === 'number' ? d.maxSeats : null,
    usedSeats: typeof d.usedSeats === 'number' ? d.usedSeats : 0,
    holdsSeat: d.holdsSeat === true,
    reason: String(d.reason ?? ''),
  };
}

export async function fetchSoftphoneEntitlement(): Promise<SoftphoneEntitlement | undefined> {
  const res = await axiosInstance.get(API_ENDPOINTS.SOFTPHONE_ENTITLEMENT);
  return parseEntitlement(res.data);
}

/**
 * Undefined while loading, with no session, or if the request failed. Callers
 * treat undefined as "unknown": the nav keeps the link (labels fail open), and
 * the server still refuses the credential (data fails closed).
 */
export function useSoftphoneEntitlement(): SoftphoneEntitlement | undefined {
  const { user, currentOrganization } = useAuth();
  const orgId = (currentOrganization as any)?.id ?? (user as any)?.organization_id ?? null;

  const [value, setValue] = useState<SoftphoneEntitlement | undefined>(
    orgId ? cache.get(orgId)?.value : undefined,
  );

  useEffect(() => {
    if (!orgId || !user) { setValue(undefined); return; }
    let alive = true;
    const entry = cache.get(orgId) ?? {};
    cache.set(orgId, entry);
    if (entry.value) { setValue(entry.value); return; }

    if (!entry.inflight) {
      entry.inflight = fetchSoftphoneEntitlement()
        .then(v => { entry.value = v; })
        .catch(() => { /* unknown — see above */ })
        .finally(() => { entry.inflight = undefined; });
    }
    entry.inflight.then(() => { if (alive) setValue(cache.get(orgId)?.value); });
    return () => { alive = false; };
  }, [orgId, user]);

  return value;
}
